import EarnLogo from '@assets/icons/earn.svg'
import OdinLogo from '@assets/icons/odin.webp'
import Carousel from '@components/Carousel'
import JupiterLogo from '@plug-core/assets/icons/jupiter.png'
import LifiLogo from '@plug-core/assets/icons/lifi.jpeg'
import LiquidiumLogo from '@plug-core/assets/icons/liquidium.png'
import WaterNeuronLogo from '@plug-core/assets/icons/waterneuron.png'
import { ETH_BASE_ID, ODINBTC_ID, SOL_ID } from '@plug-core/constants/tokenIds'
import { usePromo } from '@plug-core/state'
import { Avatar, IconButton } from '@ui'
import { ROUTES } from '@utils/routes'
import React, { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { HiXMark } from 'react-icons/hi2'
import { useNavigate } from 'react-router-dom'

interface PromoItem {
  id: string
  title: string
  description: string
  image: any
  route: string
  navState?: any
}

const PROMO_FEATURES: PromoItem[] = [
  {
    id: '8',
    title: 'LI.FI Earn is here',
    description: 'Maximize your crypto earnings with LI.FI Earn',
    image: EarnLogo,
    route: ROUTES.EARN,
  },
  {
    id: '7',
    title: 'Borrow & Lend with Liquidium',
    description: 'Borrow crypto and earn APY with ease',
    image: LiquidiumLogo,
    route: ROUTES.BORROW_LEND,
  },
  {
    id: '6',
    title: 'Perpetual futures',
    description: 'Go Long or Short with built-in Hyperliquid Perps',
    image: 'https://plug-cdn.s3.amazonaws.com/chain-logos/hyperliquid.png',
    route: ROUTES.PERPS,
  },
  {
    id: '5',
    title: 'Cross-Chain swaps enabled',
    description: 'Use LI.FI to swap ETH, SOL, ARB and more 😎',
    image: LifiLogo,
    route: ROUTES.SWAP,
    navState: {
      fromTokenId: ETH_BASE_ID,
      toTokenId: SOL_ID,
    },
  },
  {
    id: '4',
    title: 'Swap Solana tokens now!',
    description: 'Use Jupiter DEX to swap Solana ecosystem tokens',
    image: JupiterLogo,
    route: ROUTES.SWAP,
    navState: {
      fromTokenId: SOL_ID,
    },
  },
  {
    id: '1',
    title: 'Stake ICP with 14% APY',
    description: 'Try Waterneuron DAO liquid staking',
    image: WaterNeuronLogo,
    route: ROUTES.STAKE,
  },
  {
    id: '2',
    title: 'Odin swaps are here!',
    description: 'Trade 200+ Odin runes in a few clicks',
    image: OdinLogo,
    route: ROUTES.SWAP,
    navState: {
      fromTokenId: ODINBTC_ID,
    },
  },
]

interface PromoItemProps {
  item: PromoItem
  onDismiss: (id: string) => void
  width: number
}

const PromoItem: FC<PromoItemProps> = ({ item, onDismiss, width }) => {
  const navigate = useNavigate()
  const { t } = useTranslation()

  return (
    <div className="relative my-1 flex" style={{ width }}>
      <button
        type="button"
        onClick={() => navigate(item.route, { state: item.navState })}
        className="flex h-[90px] w-full cursor-pointer items-center gap-3 rounded-lg border border-solid border-hairline bg-surface py-3 pr-12 pl-4 text-left font-text text-fg transition-[background,transform] duration-150 hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus active:scale-[0.99] motion-reduce:active:scale-100"
      >
        <Avatar src={item.image} size={40} />
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="truncate text-[15px] leading-[1.3] font-semibold">{item.title}</span>
          <span className="line-clamp-2 text-[13px] leading-[1.4] text-fg-2">
            {item.description}
          </span>
        </span>
      </button>
      <IconButton
        aria-label={t('common.dismiss')}
        size="sm"
        className="absolute top-2 right-2 text-fg-2"
        onClick={(e) => {
          e.stopPropagation()
          onDismiss(item.id)
        }}
      >
        <HiXMark />
      </IconButton>
    </div>
  )
}

const Promo = () => {
  const { closePromo, closedPromos } = usePromo()
  const [container, setContainer] = useState<HTMLDivElement | null>(null)
  const [width, setWidth] = useState(0)

  // Each promo is exactly one container wide; re-measure when the layout changes.
  useEffect(() => {
    if (!container) return
    const observer = new ResizeObserver(([entry]) => setWidth(Math.floor(entry.contentRect.width)))
    observer.observe(container)
    return () => observer.disconnect()
  }, [container])

  const availablePromos = PROMO_FEATURES.filter((p) => !closedPromos.includes(p.id))

  return (
    <div ref={setContainer}>
      {width > 0 && availablePromos.length > 0 && (
        <div className="mt-4">
          <Carousel itemSize={width} spacingPx={16} hideControls showPager>
            {availablePromos.map((promo) => (
              <PromoItem key={promo.id} item={promo} onDismiss={closePromo} width={width} />
            ))}
          </Carousel>
        </div>
      )}
    </div>
  )
}

export default Promo
